import request from '../../utils/request';
import { Result } from '@/models/general';
import {
  RefereeView,
  RefereeListView,
  RefereeListQuery,
  RefereeRecommendationView,
  RefereeScheduleView,
  AssignmentHistoryView,
  AssignmentResultView,
  AssignmentResponseResultView,
  RespondToAssignmentInput,
  AssignRefereeInput,
  CreateRefereeProfileInput,
  UpdateRefereeProfileInput,
  MyAssignmentsView,
  MyAssignmentsQuery,
  MyWorkPointsSummaryView,
  RefereeAvailability,
} from '@/models/tournament';

export async function getReferees(query: RefereeListQuery) {
  const response = await request.get<Result<RefereeListView>>('Referee/GetReferees', { params: query });
  return response.data;
}

/** Server clamps `PageSize` to 1..100 (`RefereeService.GetReferees`). Asking for more is silently capped. */
const REFEREE_PAGE_SIZE = 100;
/** Runaway guard: 100 pages x 100 referees is far past any real tenant. */
const REFEREE_MAX_PAGES = 100;

/**
 * Every "load them all for a picker/table" caller was passing a single large `pageSize` (50, 100, 200,
 * even 500) and trusting it. The server clamps to 100, so a tenant with more referees than the cap
 * silently lost everyone past it — they simply never appeared in the assignment picker, the referee
 * filter or the workload table, with no empty state and no error to hint at it. Delta hit this at 129
 * referees: the assignment centre asked for 50 and could not assign the other 79.
 *
 * Walk every page instead. Mirrors `getCompensationReportAllPages`.
 */
export async function getRefereesAllPages(query: Omit<RefereeListQuery, 'page' | 'pageSize'> = {}) {
  const first = await getReferees({ ...query, page: 1, pageSize: REFEREE_PAGE_SIZE });
  if (first.errors.length > 0 || !first.payload) return first;

  const items = [...first.payload.items];
  const totalPages = Math.min(first.payload.totalPages ?? 1, REFEREE_MAX_PAGES);

  for (let page = 2; page <= totalPages; page++) {
    const next = await getReferees({ ...query, page, pageSize: REFEREE_PAGE_SIZE });
    if (next.errors.length > 0 || !next.payload) return next; // surface the failure, never a half list
    if (next.payload.items.length === 0) break;
    items.push(...next.payload.items);
  }

  return {
    ...first,
    payload: { ...first.payload, items, page: 1, pageSize: items.length },
  };
}

export async function getReferee(id: number) {
  const response = await request.get<Result<RefereeView>>(`Referee/GetReferee/${id}`);
  return response.data;
}

export async function createRefereeProfile(input: CreateRefereeProfileInput) {
  const response = await request.post<Result<RefereeView>>('Referee/CreateRefereeProfile', input);
  return response.data;
}

export async function updateRefereeProfile(input: UpdateRefereeProfileInput) {
  const response = await request.put<Result<RefereeView>>('Referee/UpdateRefereeProfile', input);
  return response.data;
}

export async function assignReferee(input: AssignRefereeInput) {
  const response = await request.post<Result<AssignmentResultView>>('Referee/AssignReferee', input);
  return response.data;
}

export async function getRecommendedReferees(matchId: number) {
  const response = await request.get<Result<RefereeRecommendationView[]>>(`Referee/GetRecommendedReferees/${matchId}`);
  return response.data;
}

export async function getRefereeSchedule(id: number, startDate?: string, endDate?: string) {
  const response = await request.get<Result<RefereeScheduleView[]>>(`Referee/GetRefereeSchedule/${id}`, {
    params: { startDate, endDate },
  });
  return response.data;
}

export async function getAssignmentHistory(matchId: number) {
  const response = await request.get<Result<AssignmentHistoryView[]>>(`Referee/GetAssignmentHistory/${matchId}`);
  return response.data;
}

export async function getMyAssignments(query?: MyAssignmentsQuery) {
  const response = await request.get<Result<MyAssignmentsView>>('Referee/GetMyAssignments', { params: query });
  return response.data;
}

/**
 * Every one of my assignments, not just the first server page. `GetMyAssignments` clamps `PageSize`
 * to 1..100 (`RefereeService.GetMyAssignments`), and the my-assignments view paginates CLIENT-side
 * over whatever it received — so a busy referee past 100 assignments silently lost the tail from
 * both the upcoming and past tabs.
 */
export async function getMyAssignmentsAllPages(query?: Omit<MyAssignmentsQuery, 'page' | 'pageSize'>) {
  const first = await getMyAssignments({ ...query, page: 1, pageSize: REFEREE_PAGE_SIZE });
  if (first.errors.length > 0 || !first.payload) return first;

  const items = [...first.payload.items];
  const totalPages = Math.min(first.payload.totalPages ?? 1, REFEREE_MAX_PAGES);

  for (let page = 2; page <= totalPages; page++) {
    const next = await getMyAssignments({ ...query, page, pageSize: REFEREE_PAGE_SIZE });
    if (next.errors.length > 0 || !next.payload) return next; // surface the failure, never a half list
    if (next.payload.items.length === 0) break;
    items.push(...next.payload.items);
  }

  return {
    ...first,
    payload: { ...first.payload, items, page: 1, pageSize: items.length },
  };
}

export async function getMySchedule(startDate?: string, endDate?: string) {
  const response = await request.get<Result<RefereeScheduleView[]>>('Referee/GetMySchedule', {
    params: { startDate, endDate },
  });
  return response.data;
}

export async function getMyWorkPoints(query?: { startDate?: string; endDate?: string }) {
  const response = await request.get<Result<MyWorkPointsSummaryView>>('Referee/GetMyWorkPoints', { params: query });
  return response.data;
}

export async function getMyProfile() {
  const response = await request.get<Result<RefereeView>>('Referee/GetMyProfile');
  return response.data;
}

export interface AvailabilityChange {
  date: string;
  status: RefereeAvailability;
}

export async function setMyAvailability(changes: AvailabilityChange[]) {
  const response = await request.post<Result<RefereeScheduleView[]>>('Referee/SetMyAvailability', { changes });
  return response.data;
}

export async function acceptAssignment(input: RespondToAssignmentInput) {
  const response = await request.post<Result<AssignmentResponseResultView>>('Referee/AcceptAssignment', input);
  return response.data;
}

export async function declineAssignment(input: RespondToAssignmentInput) {
  const response = await request.post<Result<AssignmentResponseResultView>>('Referee/DeclineAssignment', input);
  return response.data;
}

export interface CompanyUser {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  fullName?: string;
  firstName?: string;
  lastName?: string;
}

// Raw shape of UserView as returned by GET /Account/Users
interface RawCompanyUser {
  id: string;
  fullName?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
}

export async function getCompanyUsers(): Promise<Result<CompanyUser[]>> {
  const response = await request.get<Result<RawCompanyUser[]>>('Account/Users', {
    params: { scope: 'Company', id: 0 },
  });
  const { data } = response;
  const mapped: CompanyUser[] = (data.payload ?? []).map((u) => {
    const derivedName =
      (u.fullName && u.fullName.trim()) ||
      [u.firstName, u.lastName].filter(Boolean).join(' ').trim() ||
      u.email ||
      '';
    return {
      id: u.id,
      name: derivedName,
      email: u.email,
      phone: u.phoneNumber,
    };
  });
  return { ...data, payload: mapped } as Result<CompanyUser[]>;
}
